import * as React from "react";
import { capitaliseSentence } from "./capitaliseSentence";
import { ChevronDown, Plus, Search, XCircle } from "lucide-react";
import {
    DEFAULT_SELECT_FIELD_SIZE,
    selectFieldMultiSelectTriggerClassName,
    selectFieldTriggerTextClassName,
} from "./select";
import { cn } from "./utils";
import { Badge } from "./badge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./dialog";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { ScrollArea } from "./scroll-area";

export interface MultiSelectOption {
    label: string;
    value: string;
    /** Secondary line shown in the popover list (e.g. start date). */
    description?: string;
    /** Extra text included in popover search (e.g. uuid). */
    searchText?: string;
    /** Optional leading icon in the list and selected chips. */
    icon?: React.ReactNode;
    /** Heading shown above consecutive options that share it. */
    group?: string;
    /** Shown but not selectable. */
    disabled?: boolean;
}

export interface MultiSelectAddOptionActions {
    close: () => void;
}

export interface MultiSelectProps {
    options: MultiSelectOption[];
    onValueChange: (value: string[]) => void;
    defaultValue?: string[];
    value?: string[];
    placeholder?: string;
    className?: string;
    popoverContentClassName?: string;
    maxCount?: number;
    disabled?: boolean;
    /** Renders in a dialog opened from the footer Add button (ComboBox-style). */
    addOption?: React.ReactNode | ((actions: MultiSelectAddOptionActions) => React.ReactNode);
    addOptionTitle?: string;
    addOptionTriggerLabel?: string;
    /** Custom chip content for a selected value (e.g. rich template details). */
    renderSelectedBadge?: (value: string) => React.ReactNode;
    /** Accessible label for remove buttons when `renderSelectedBadge` is used. */
    getSelectedAriaLabel?: (value: string) => string;
    /** Selected values that cannot be removed (e.g. template-required grants). */
    lockedValues?: string[];
    /**
     * `ghost` previews the selection as plain badges. Clicking opens the list
     * without a bordered field.
     */
    variant?: "default" | "ghost";
    /** Optional test id on the combobox trigger. */
    "data-testid"?: string;
}

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
    (
        {
            options,
            onValueChange,
            defaultValue = [],
            value: controlledValue,
            placeholder = "Select options",
            className,
            popoverContentClassName,
            maxCount: _maxCount,
            disabled = false,
            addOption,
            addOptionTitle = "Add an option",
            addOptionTriggerLabel = "Add",
            renderSelectedBadge,
            getSelectedAriaLabel,
            lockedValues = [],
            variant = "default",
            "data-testid": dataTestId,
        },
        ref
    ) => {
        const selectAllId = React.useId();
        const listPanelId = React.useId();
        const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);
        const [open, setOpen] = React.useState(false);
        const [addOptionOpen, setAddOptionOpen] = React.useState(false);
        const [search, setSearch] = React.useState("");
        const searchRef = React.useRef<HTMLInputElement>(null);

        const isControlled = controlledValue !== undefined;
        const selectedValues = isControlled ? controlledValue : internalValue;
        const lockedSet = React.useMemo(() => new Set(lockedValues), [lockedValues]);

        const prevDefaultRef = React.useRef(defaultValue);
        React.useEffect(() => {
            if (isControlled) return;
            if (
                prevDefaultRef.current.length !== defaultValue.length ||
                !prevDefaultRef.current.every((v, i) => v === defaultValue[i])
            ) {
                setInternalValue(defaultValue);
                prevDefaultRef.current = defaultValue;
            }
        }, [defaultValue, isControlled]);

        React.useEffect(() => {
            if (!open) setSearch("");
        }, [open]);

        const safeOptions = React.useMemo(() => options ?? [], [options]);
        const selectableOptions = React.useMemo(() => safeOptions.filter((o) => !o.disabled), [safeOptions]);

        const filteredOptions = React.useMemo(() => {
            if (!search) return safeOptions;
            const q = search.toLowerCase();
            return safeOptions.filter((o) => {
                const haystack = [o.label, o.value, o.description, o.searchText]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(q);
            });
        }, [safeOptions, search]);

        const updateValue = React.useCallback(
            (next: string[]) => {
                const withLocks = [...new Set([...lockedValues, ...next])];
                if (!isControlled) setInternalValue(withLocks);
                onValueChange(withLocks);
            },
            [isControlled, lockedValues, onValueChange]
        );

        const toggleOption = (optionValue: string) => {
            if (disabled) return;
            if (safeOptions.find((o) => o.value === optionValue)?.disabled) return;
            if (lockedSet.has(optionValue) && selectedValues.includes(optionValue)) return;
            const next = selectedValues.includes(optionValue)
                ? selectedValues.filter((v) => v !== optionValue)
                : [...selectedValues, optionValue];
            updateValue(next);
        };

        const removeValue = (value: string) => {
            if (disabled || lockedSet.has(value)) return;
            updateValue(selectedValues.filter((v) => v !== value));
        };

        const handleClear = () => {
            if (disabled) return;
            updateValue([...lockedValues]);
        };

        const handleSelectAll = (checked: boolean) => {
            if (disabled) return;
            updateValue(checked ? selectableOptions.map((o) => o.value) : [...lockedValues]);
        };

        const getOption = (value: string) => safeOptions.find((o) => o.value === value);
        const getLabel = (value: string) => getOption(value)?.label ?? value;

        const isAllSelected = selectableOptions.length > 0 && selectedValues.length === selectableOptions.length;
        const isIndeterminate = selectedValues.length > 0 && selectedValues.length < selectableOptions.length;
        const showSelectAll = safeOptions.length > 1;

        const addOptionContent =
            typeof addOption === "function" ? addOption({ close: () => setAddOptionOpen(false) }) : addOption;

        return (
            <Popover open={open} onOpenChange={setOpen} modal={false}>
                <PopoverTrigger asChild>
                    <div
                        ref={ref}
                        role="combobox"
                        aria-expanded={open}
                        aria-controls={open ? listPanelId : undefined}
                        data-testid={dataTestId}
                        className={cn(
                            variant === "ghost"
                                ? cn(
                                      "box-border flex items-center gap-1 bg-transparent hover:bg-muted/70 px-1.5 py-0.5 border border-transparent rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 w-auto min-w-0 max-w-full min-h-8 font-normal text-left cursor-pointer",
                                      open && "bg-muted/70"
                                  )
                                : selectFieldMultiSelectTriggerClassName(DEFAULT_SELECT_FIELD_SIZE),
                            disabled && "opacity-50 cursor-not-allowed",
                            className
                        )}
                        aria-label={
                            variant === "ghost" && selectedValues.length > 0
                                ? selectedValues.map((value) => getLabel(value)).join(", ")
                                : placeholder
                        }
                    >
                        <div className="flex flex-wrap flex-1 items-center gap-1 min-w-0">
                            {selectedValues.map((value) => {
                                const option = getOption(value);
                                const rawLabel = getSelectedAriaLabel?.(value) ?? getLabel(value);
                                if (variant === "ghost") {
                                    return (
                                        <Badge
                                            key={value}
                                            variant="outline"
                                            className="flex items-center gap-1 bg-white dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 font-normal normal-case whitespace-nowrap"
                                        >
                                            {option?.icon ? (
                                                <span className="[&_svg]:size-4 shrink-0">{option.icon}</span>
                                            ) : null}
                                            <span className="truncate">{rawLabel}</span>
                                            {open && !lockedSet.has(value) && !disabled ? (
                                                <button
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        removeValue(value);
                                                    }}
                                                    className="hover:bg-muted ml-0.5 p-0.5 rounded-full cursor-pointer shrink-0"
                                                    aria-label={`Remove ${rawLabel}`}
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                </button>
                                            ) : null}
                                        </Badge>
                                    );
                                }
                                return (
                                    <Badge
                                        key={value}
                                        variant="secondary"
                                        className={cn(
                                            "flex gap-1.5 py-1 min-w-0 max-w-full hover:scale-105 transition-all hover:-translate-y-0.5 duration-300 ease-in-out",
                                            renderSelectedBadge ? "items-start py-1.5" : "items-center",
                                            selectedValues.length === 1 && "shrink min-w-0"
                                        )}
                                    >
                                        {renderSelectedBadge ? (
                                            <div className="flex-1 min-w-0">{renderSelectedBadge(value)}</div>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                                {option?.icon ? (
                                                    <span className="[&_svg]:size-4 shrink-0">{option.icon}</span>
                                                ) : null}
                                                <span className="truncate capitalize">
                                                    {option?.icon
                                                        ? rawLabel
                                                        : capitaliseSentence(String(rawLabel), "-")}
                                                </span>
                                            </span>
                                        )}
                                        {lockedSet.has(value) || disabled ? null : (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeValue(value);
                                                }}
                                                className="hover:bg-indigo-200 dark:hover:bg-indigo-800 ml-0.5 p-0.5 rounded-full cursor-pointer shrink-0"
                                                aria-label={`Remove ${rawLabel}`}
                                            >
                                                <XCircle className="w-4 h-4" />
                                            </button>
                                        )}
                                    </Badge>
                                );
                            })}
                            {selectedValues.length === 0 ? (
                                <span
                                    className={cn(
                                        variant === "ghost"
                                            ? "text-muted-foreground text-sm"
                                            : cn(
                                                  selectFieldTriggerTextClassName(),
                                                  "text-slate-700 dark:text-slate-400 text-sm"
                                              )
                                    )}
                                >
                                    {placeholder}
                                </span>
                            ) : null}
                        </div>
                        {variant === "ghost" ? (
                            open ? (
                                <ChevronDown className="opacity-60 w-4 h-4 shrink-0" />
                            ) : null
                        ) : (
                            <ChevronDown className="opacity-75 w-4 h-4 shrink-0" />
                        )}
                    </div>
                </PopoverTrigger>
                <PopoverContent
                    id={listPanelId}
                    className={cn("p-0 w-[var(--radix-popover-trigger-width)]", popoverContentClassName)}
                    align="start"
                    onOpenAutoFocus={(event) => {
                        event.preventDefault();
                        searchRef.current?.focus();
                    }}
                >
                    <div className="flex items-center gap-2 px-2 py-1 border-b text-muted-foreground">
                        <Search className="size-4 shrink-0" aria-hidden />
                        <input
                            ref={searchRef}
                            placeholder="Filter options..."
                            aria-label="Filter options..."
                            className="flex-1 bg-transparent px-0 py-1 border-0 outline-none focus-visible:outline-none ring-0 focus-visible:ring-0 min-w-0 dark:placeholder:text-slate-400 placeholder:text-slate-700 text-sm"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Escape") {
                                    event.preventDefault();
                                    setOpen(false);
                                }
                            }}
                        />
                    </div>
                    {showSelectAll && !search && (
                        <div className="flex items-center px-4 pt-1 pb-3">
                            <Checkbox
                                checked={isAllSelected || isIndeterminate}
                                onCheckedChange={handleSelectAll}
                                id={selectAllId}
                                className={cn("mr-2", isIndeterminate && "opacity-50")}
                            />
                            <Label htmlFor={selectAllId} className="font-normal text-sm leading-none">
                                {isAllSelected ? "Deselect All" : isIndeterminate ? "Some selected" : "Select All"}
                            </Label>
                        </div>
                    )}
                    <ScrollArea className="h-[300px]">
                        <ul className="flex flex-col px-2">
                            {filteredOptions.map((option, index) => {
                                const isSelected = selectedValues.includes(option.value);
                                const isOptionDisabled = Boolean(option.disabled);
                                const startsGroup = option.group && option.group !== filteredOptions[index - 1]?.group;
                                return (
                                    <React.Fragment key={option.value}>
                                        {startsGroup ? (
                                            <li
                                                role="presentation"
                                                className={cn(
                                                    "px-1 pb-0 font-normal text-slate-600 dark:text-slate-300 text-xs uppercase",
                                                    index > 0 ? "pt-2 border-t" : "pt-0"
                                                )}
                                            >
                                                {option.group}
                                            </li>
                                        ) : null}
                                        <li
                                            data-testid={
                                                dataTestId ? `${dataTestId}-option-${option.value}` : undefined
                                            }
                                            aria-disabled={isOptionDisabled || undefined}
                                            className={cn(
                                                "flex items-center gap-2 hover:bg-accent p-2 rounded-sm cursor-pointer",
                                                isOptionDisabled && "opacity-50 pointer-events-none"
                                            )}
                                            onClick={() => toggleOption(option.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    toggleOption(option.value);
                                                }
                                            }}
                                            tabIndex={isOptionDisabled ? -1 : 0}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                disabled={isOptionDisabled}
                                                onCheckedChange={() => toggleOption(option.value)}
                                                tabIndex={-1}
                                            />
                                            <div className="flex flex-col min-w-0">
                                                <span className="inline-flex items-center gap-2 min-w-0">
                                                    {option.icon ? (
                                                        <span className="[&_svg]:size-4 shrink-0">{option.icon}</span>
                                                    ) : null}
                                                    <Label className="font-normal truncate cursor-pointer">
                                                        {option.label}
                                                    </Label>
                                                </span>
                                                {option.description ? (
                                                    <span className="text-muted-foreground text-xs truncate">
                                                        {option.description}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </li>
                                    </React.Fragment>
                                );
                            })}
                            {filteredOptions.length === 0 && (
                                <li className="py-4 text-muted-foreground text-sm text-center">No results found.</li>
                            )}
                        </ul>
                    </ScrollArea>
                    <div className="flex flex-wrap gap-2 p-3 border-t">
                        <Button type="button" variant="outline" size="sm" onClick={handleClear} disabled={disabled}>
                            Clear
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                            Close
                        </Button>
                        {addOptionContent ? (
                            <Dialog open={addOptionOpen} onOpenChange={setAddOptionOpen}>
                                <DialogTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center gap-1.5"
                                        disabled={disabled}
                                    >
                                        <Plus className="w-4 h-4" />
                                        {addOptionTriggerLabel}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>{addOptionTitle}</DialogTitle>
                                    </DialogHeader>
                                    {addOptionContent}
                                </DialogContent>
                            </Dialog>
                        ) : null}
                    </div>
                </PopoverContent>
            </Popover>
        );
    }
);

MultiSelect.displayName = "MultiSelect";
